import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import {
  PageHeader,
  Card,
  SectionHeader,
  Badge,
  StatusDot,
  KeyValue,
  Spinner,
  Button,
  QueryError,
} from "../components";
import { PluginHealthPanel } from "../components/PluginHealthPanel";
import { usePolling } from "../hooks/usePolling";
import { apiGet } from "../lib/api";
import {
  formatDateTime,
  formatFleetHealthReason,
  formatFleetRecommendedAction,
  formatFleetStatusLabel,
} from "../lib/display";

interface Deployment {
  id: string;
  tenantId: string;
  tenantName: string;
  health: "healthy" | "degraded" | "unhealthy" | "offline" | "unknown";
  healthReason?: string | null;
  healthStateSince?: number | string | null;
  recommendedAction?: string | null;
  status: string;
  version: string;
  uptime: number;
  activeSessions: number;
  episodesToday: number;
  gatewayUrl: string;
  hankUrl: string;
  adminUrl: string;
  lastHealthAt: number | string | null;
  createdAt: number | string;
}

interface TelemetryPoint {
  timestamp: string;
  cpuPercent: number;
  memoryPercent: number;
  requestsPerMin: number;
  errorsPerMin: number;
  costPerMin: number;
  activeConnections: number;
  p50LatencyMs: number;
  p99LatencyMs: number;
}

interface TelemetryData {
  deploymentId: string;
  interval: string;
  points: TelemetryPoint[];
}

// Plugin telemetry wire types previously lived here. They moved to
// `../components/PluginHealthPanel.tsx` in BUCKET I.7 along with the rendering
// logic — the component is now the type owner (P11 per-component ownership).

const STATUS_DOT_MAP: Record<string, "healthy" | "degraded" | "down"> = {
  healthy: "healthy",
  degraded: "degraded",
  unhealthy: "down",
  unknown: "degraded",
  offline: "down",
};

const STATUS_BADGE_COLOR_MAP: Record<string, "green" | "amber" | "red"> = {
  healthy: "green",
  degraded: "amber",
  unhealthy: "red",
  unknown: "amber",
  offline: "red",
};

function formatTime(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

const tooltipStyle = {
  backgroundColor: "var(--c-surface)",
  border: "1px solid var(--c-border)",
  borderRadius: 8,
  fontSize: 12,
};

export default function DeploymentHealth() {
  const { id } = useParams<{ id: string }>();

  const {
    data: deployments,
    isLoading: loadingDeployments,
    error: errorDeployments,
  } = useQuery<Deployment[]>({
    queryKey: ["fleet", "deployments"],
    queryFn: async () => {
      const data = await apiGet<Deployment[] | { deployments?: Deployment[] }>(
        "/api/fleet/deployments",
      );
      return Array.isArray(data) ? data : (data.deployments ?? []);
    },
  });

  const { data: telemetry, isLoading: loadingTelemetry } = useQuery<TelemetryData>({
    queryKey: ["telemetry", id],
    queryFn: () => apiGet<TelemetryData>(`/api/telemetry/${id}`),
    enabled: !!id,
  });

  usePolling(
    [
      ["fleet", "deployments"],
      ["telemetry", id ?? ""],
    ],
    15_000,
  );

  const deployment = deployments?.find((d) => d.id === id);
  const isLoading = loadingDeployments || loadingTelemetry;

  // Build cumulative cost data
  const costData = useMemo(() => {
    if (!telemetry?.points) return [];
    let cumulative = 0;
    return telemetry.points.map((p) => {
      cumulative += p.costPerMin;
      return {
        timestamp: p.timestamp,
        cumulativeCost: Math.round(cumulative * 100) / 100,
      };
    });
  }, [telemetry]);

  // Latest data point for latency
  const latestPoint = telemetry?.points?.[telemetry.points.length - 1];

  if (isLoading && !deployment) {
    return (
      <div>
        <PageHeader title="Deployment Health" subtitle="Loading deployment data..." />
        <div className="flex items-center justify-center py-20">
          <Spinner size={24} />
        </div>
      </div>
    );
  }

  if (errorDeployments && !deployment) {
    return (
      <div>
        <PageHeader title="Deployment Health" subtitle="Loading deployment data..." />
        <QueryError
          error={errorDeployments}
          queryKeys={[
            ["fleet", "deployments"],
            ["telemetry", id ?? ""],
          ]}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Back link */}
      <div className="mb-4">
        <Link to="/fleet" style={{ textDecoration: "none" }}>
          <Button variant="ghost" size="sm">
            &larr; Back to Fleet Overview
          </Button>
        </Link>
      </div>

      {/* Header with deployment info */}
      <PageHeader
        title={deployment ? `${deployment.tenantName} (${id})` : (id ?? "Unknown")}
        subtitle={
          deployment
            ? `v${deployment.version} · Uptime ${deployment.uptime}%`
            : "Deployment not found"
        }
      />

      {/* Status badges */}
      {deployment && (
        <div className="flex items-center gap-3 mb-8">
          <StatusDot status={STATUS_DOT_MAP[deployment.health] ?? "down"} />
          <Badge color={STATUS_BADGE_COLOR_MAP[deployment.health] ?? "amber"} dot>
            {formatFleetStatusLabel(deployment.health)}
          </Badge>
          <span className="text-xs" style={{ color: "var(--c-text-secondary)" }}>
            {deployment.activeSessions} active session{deployment.activeSessions !== 1 ? "s" : ""}
          </span>
          <span className="text-xs" style={{ color: "var(--c-text-faint)" }}>
            {deployment.episodesToday} episodes today
          </span>
        </div>
      )}

      {/* Time-series charts 2x2 grid */}
      {deployment && (
        <Card className="mb-6">
          <SectionHeader title="Deployment Identity" />
          <div
            className="mb-4 rounded-lg px-4 py-3"
            style={{
              backgroundColor:
                deployment.health === "healthy"
                  ? "rgba(34, 197, 94, 0.08)"
                  : deployment.health === "degraded" || deployment.health === "unknown"
                    ? "rgba(234, 179, 8, 0.1)"
                    : "rgba(239, 68, 68, 0.08)",
              border:
                deployment.health === "healthy"
                  ? "1px solid rgba(34, 197, 94, 0.18)"
                  : deployment.health === "degraded" || deployment.health === "unknown"
                    ? "1px solid rgba(234, 179, 8, 0.2)"
                    : "1px solid rgba(239, 68, 68, 0.18)",
            }}
          >
            <div className="text-sm font-medium mb-1" style={{ color: "var(--c-text)" }}>
              {formatFleetHealthReason(deployment.healthReason)}
            </div>
            <div className="text-xs mb-2" style={{ color: "var(--c-text-secondary)" }}>
              {formatFleetRecommendedAction(deployment.recommendedAction)}
            </div>
            <div className="text-[11px]" style={{ color: "var(--c-text-faint)" }}>
              Status since {formatDateTime(deployment.healthStateSince, "unknown")}
            </div>
          </div>
          <KeyValue label="Lifecycle status" value={deployment.status} />
          <KeyValue label="Gateway URL" value={deployment.gatewayUrl} />
          <KeyValue label="Hank URL" value={deployment.hankUrl} />
          <KeyValue label="Adam URL" value={deployment.adminUrl} />
          <KeyValue
            label="Last Heartbeat"
            value={
              deployment.lastHealthAt
                ? formatDateTime(deployment.lastHealthAt)
                : "No heartbeat recorded"
            }
          />
        </Card>
      )}

      <SectionHeader title="Resource Metrics" subtitle="Last 60 minutes, 1-minute intervals" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        {/* CPU chart */}
        <Card>
          <p className="text-xs font-medium mb-3" style={{ color: "var(--c-text-secondary)" }}>
            CPU %
          </p>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={telemetry?.points ?? []}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
              <XAxis
                dataKey="timestamp"
                tickFormatter={formatTime}
                tick={{ fontSize: 10, fill: "var(--c-text-faint)" }}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: 10, fill: "var(--c-text-faint)" }}
                width={35}
              />
              <RechartsTooltip contentStyle={tooltipStyle} labelFormatter={formatTime} />
              <Line
                type="monotone"
                dataKey="cpuPercent"
                stroke="#1A61ED"
                strokeWidth={2}
                dot={false}
                name="CPU %"
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Memory chart */}
        <Card>
          <p className="text-xs font-medium mb-3" style={{ color: "var(--c-text-secondary)" }}>
            Memory %
          </p>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={telemetry?.points ?? []}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
              <XAxis
                dataKey="timestamp"
                tickFormatter={formatTime}
                tick={{ fontSize: 10, fill: "var(--c-text-faint)" }}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: 10, fill: "var(--c-text-faint)" }}
                width={35}
              />
              <RechartsTooltip contentStyle={tooltipStyle} labelFormatter={formatTime} />
              <Line
                type="monotone"
                dataKey="memoryPercent"
                stroke="#F2C94C"
                strokeWidth={2}
                dot={false}
                name="Memory %"
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Requests/min chart */}
        <Card>
          <p className="text-xs font-medium mb-3" style={{ color: "var(--c-text-secondary)" }}>
            Requests / min
          </p>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={telemetry?.points ?? []}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
              <XAxis
                dataKey="timestamp"
                tickFormatter={formatTime}
                tick={{ fontSize: 10, fill: "var(--c-text-faint)" }}
                interval="preserveStartEnd"
              />
              <YAxis tick={{ fontSize: 10, fill: "var(--c-text-faint)" }} width={35} />
              <RechartsTooltip contentStyle={tooltipStyle} labelFormatter={formatTime} />
              <Line
                type="monotone"
                dataKey="requestsPerMin"
                stroke="#4CAF50"
                strokeWidth={2}
                dot={false}
                name="Requests/min"
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Errors/min chart */}
        <Card>
          <p className="text-xs font-medium mb-3" style={{ color: "var(--c-text-secondary)" }}>
            Errors / min
          </p>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={telemetry?.points ?? []}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
              <XAxis
                dataKey="timestamp"
                tickFormatter={formatTime}
                tick={{ fontSize: 10, fill: "var(--c-text-faint)" }}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 10, fill: "var(--c-text-faint)" }}
                width={35}
                allowDecimals={false}
              />
              <RechartsTooltip contentStyle={tooltipStyle} labelFormatter={formatTime} />
              <Line
                type="monotone"
                dataKey="errorsPerMin"
                stroke="#E53E3E"
                strokeWidth={2}
                dot={false}
                name="Errors/min"
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Latency section */}
      <SectionHeader title="Latency" subtitle="Latest data point" />
      <Card className="mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <KeyValue
            label="p50 Latency"
            value={latestPoint ? `${latestPoint.p50LatencyMs} ms` : "--"}
            mono
          />
          <KeyValue
            label="p99 Latency"
            value={latestPoint ? `${latestPoint.p99LatencyMs} ms` : "--"}
            mono
          />
        </div>
      </Card>

      {/* Cumulative cost chart */}
      <SectionHeader title="Cumulative Cost" subtitle="Running total over last 60 minutes" />
      <Card>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={costData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
            <XAxis
              dataKey="timestamp"
              tickFormatter={formatTime}
              tick={{ fontSize: 10, fill: "var(--c-text-faint)" }}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 10, fill: "var(--c-text-faint)" }}
              width={45}
              tickFormatter={(v: number) => `$${v.toFixed(2)}`}
            />
            <RechartsTooltip
              contentStyle={tooltipStyle}
              labelFormatter={formatTime}
              formatter={(value: number) => [`$${value.toFixed(2)}`, "Cost"]}
            />
            <Area
              type="monotone"
              dataKey="cumulativeCost"
              stroke="#1A61ED"
              fill="#1A61ED"
              fillOpacity={0.1}
              strokeWidth={2}
              name="Cumulative Cost"
            />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      {/* Plugin health — rendered below the telemetry charts. Panel owns its
          own query, wire types, and truthful empty state (P4 + P11). */}
      {id && (
        <div className="mt-6">
          <PluginHealthPanel deploymentId={id} />
        </div>
      )}
    </div>
  );
}
